import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd, Params } from '@angular/router';
import { filter, take } from 'rxjs';
import { CountryService } from '@core/services/country-service/country-service';
import { UserConnectedLocationService } from '@core/services/user-connected-location-service/user-connected-location-service';

@Injectable({
  providedIn: 'root',
})
export class UrlSyncService {
  private readonly router = inject(Router);
  private readonly countryService = inject(CountryService);
  private readonly userConnectedLocationsInfoService = inject(UserConnectedLocationService);

  private readonly ignoredUrls = ['/login', '/profile', '/token', '/auth', '/logout'];
  private isUpdatingUrl = false;
  private hasInitialized = false;

  initialize(): void {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    // Listen to router events
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.handleNavigation());

    // Listen to country changes to update URL
    this.countryService.selectedCountry$.subscribe((country) => {
      if (country) {
        this.syncLevel0InUrl(country.uuid);
      }
    });
  }

  private syncLevel0InUrl(level0: string): void {
    // Tant que la navigation initiale (deep link) n'a pas abouti, this.router.url
    // vaut encore son état par défaut '/' — l'utiliser ici écraserait l'URL profonde
    // en cours de résolution (cf. navigate([]) plus bas qui redevient relatif à la racine).
    // On attend donc la fin de cette navigation avant de lire/écrire les query params.
    if (!this.router.navigated) {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1),
        )
        .subscribe(() => this.updateLevel0InUrl(level0, this.getCurrentQueryParams()));
      return;
    }

    this.updateLevel0InUrl(level0, this.getCurrentQueryParams());
  }

  private getCurrentQueryParams(): Params {
    return { ...this.router.parseUrl(this.router.url).queryParams };
  }

  private handleNavigation(): void {
    const currentPath = this.router.url.split('?')[0];
    const isIgnoredUrl = this.ignoredUrls.some((url) => currentPath.startsWith(url));

    if (!isIgnoredUrl) {
      if (!this.countryService.selectedCountry()) {
        // Trigger initialization if needed, though App component orchestrates the initial load
        // This acts as a fallback or for deep links
        this.initializeUserLocation();
      }
    }
  }

  private initializeUserLocation(): void {
    this.userConnectedLocationsInfoService
      .initializeUserLocation()
      .then(() => {
        // Country selection logic is handled in CountryService/UserConnectedLocationService
        // We just react to the emission in the subscription above
      })
      .catch((error) => {
        console.error('Error initializing location from UrlSyncService:', error);
      });
  }

  private updateLevel0InUrl(level0: string, currentQueryParams: Params): void {
    if (this.isUpdatingUrl) return;
    // Check if level0 is already correct to avoid unnecessary navigation
    if (currentQueryParams['level0'] === level0) return;

    this.isUpdatingUrl = true;

    const updatedQueryParams = { ...currentQueryParams, level0 };
    this.router
      .navigate([], {
        queryParams: updatedQueryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .finally(() => {
        this.isUpdatingUrl = false;
      });
  }
}
